import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { MIN_PHOTOS, PhotoManager } from "@/components/profile/PhotoManager";
import { VideoUploader } from "@/components/experience/VideoUploader";
import { openCookieSettings } from "@/components/CookieConsent";
import { EmailPrefs } from "@/components/connect/EmailPrefs";
import { CloseAccount } from "@/components/profile/CloseAccount";
import { CityField, IntentPicker, InterestPicker } from "@/pages/Apply";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function Profile() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: user.name, home_city: user.home_city, bio: user.bio, interests: user.interests, intents: user.intents, video: user.video || null, photo_focus: user.photo_focus || null, photos: user.photos?.length ? user.photos : (user.photo ? [user.photo] : []) });
  const [busy, setBusy] = useState("");
  const [videoBusy, setVideoBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (f.name.trim().length < 2 || f.home_city.trim().length < 2) return toast.error("Name and home city are required");
    if (f.photos.length < MIN_PHOTOS) return toast.error(`Please keep at least ${MIN_PHOTOS} photos`);
    setBusy("save");
    try { const { data } = await api.put("/me", f); setUser(data); toast.success("Profile saved"); } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  return (
    <div className="shell max-w-[760px] pb-24 pt-10" data-testid="profile-page">
      <p className="eyebrow mb-3">Profile</p>
      <h1 className="font-display text-5xl leading-none">Your profile</h1>
      <p className="mt-3 text-muted-foreground">Visible to approved members. Your email stays private.</p>
      <form onSubmit={save} className="mt-10 space-y-6" noValidate>
        <PhotoManager value={f.photos} onChange={set("photos")} focus={f.photo_focus} onFocus={set("photo_focus")} />
        <VideoUploader value={f.video} onChange={set("video")} onBusy={setVideoBusy} testId="profile-video" label="Intro video" addLabel="Add an intro video" />
        <div><label htmlFor="p-name" className="field-label">Full name <span className="font-normal text-muted-foreground">(members only see your first name)</span></label><input id="p-name" className="field" value={f.name} onChange={(e) => set("name")(e.target.value)} data-testid="profile-name-input" /></div>
        <div><label className="field-label">Email address</label><input className="field opacity-60" value={user.email} disabled aria-label="Email address" /></div>
        <CityField value={f.home_city} onChange={set("home_city")} />
        <IntentPicker value={f.intents} onChange={set("intents")} />
        <InterestPicker value={f.interests} onChange={set("interests")} />
        <div><label htmlFor="p-bio" className="field-label">About you</label><textarea id="p-bio" rows={5} maxLength={600} className="field py-3" value={f.bio} onChange={(e) => set("bio")(e.target.value)} data-testid="profile-bio-input" /></div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <button type="submit" className="btn-primary" disabled={!!busy || videoBusy} data-testid="profile-save-button">{busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}</button>
          <Link to={`/members/${user.id}`} className="btn-outline" data-testid="profile-view-public">View as members see it</Link>
        </div>
      </form>
      <EmailPrefs />
      <div className="mt-14 flex flex-wrap gap-4 border-t border-border pt-8">
        {user.role === "admin" && <Link to="/admin" className="btn-outline" data-testid="profile-admin-link">Review applications</Link>}
        <button type="button" className="btn-outline" onClick={openCookieSettings} data-testid="profile-cookie-settings">Cookie settings</button>
        <button type="button" className="btn-outline" onClick={async () => { await logout(); navigate("/"); }} data-testid="profile-signout-button">Sign out</button>
      </div>
      <CloseAccount />
    </div>
  );
}
